import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Typography
} from '@mui/material';
import {
  Block as BlockIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { BRAND } from '../../constants/branding';
import TenantHubLogo from '../../components/TenantHubLogo';

const DEFAULT_MESSAGE = (
  'Access denied: your IP address is not allowed to use this application. '
  + 'Please refrain from using the application. '
  + 'If you believe you need access, contact your systems administrator.'
);

const readStoredDenial = () => {
  try {
    const raw = sessionStorage.getItem('ip_access_denied');
    if (!raw) return {};
    sessionStorage.removeItem('ip_access_denied');
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
};

const IpAccessDenied = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const stored = useMemo(() => readStoredDenial(), []);
  const state = { ...stored, ...(location.state || {}) };
  const message = state.message || DEFAULT_MESSAGE;
  const ip = state.ip || '';
  const detail = state.detail || '';

  const goLogin = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        background: 'linear-gradient(160deg, #fff7ed 0%, #ffedd5 40%, #fecaca 100%)'
      }}
    >
      <Card sx={{ maxWidth: 560, width: '100%', border: '1px solid #fca5a5' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <TenantHubLogo size={40} />
            <Box>
              <Typography variant="h6" fontWeight={800}>{BRAND.productName}</Typography>
              <Typography variant="caption" color="text.secondary">Access restricted</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BlockIcon sx={{ color: '#b91c1c', fontSize: 36 }} />
            <Typography variant="h5" fontWeight={800} color="#991b1b">
              IP address not allowed
            </Typography>
          </Box>

          <Alert severity="error" sx={{ mb: 2 }}>
            {message}
          </Alert>

          {ip ? (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Detected IP:
              {' '}
              <Box component="code" sx={{ fontWeight: 700, px: 0.75, py: 0.25, bgcolor: '#fee2e2', borderRadius: 1 }}>
                {ip}
              </Box>
            </Typography>
          ) : null}

          {detail ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {detail}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Refrain from using this application. If you still think you need access, contact your systems administrator.
            </Typography>
          )}

          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={goLogin}
            sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' }}
          >
            Return to login
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default IpAccessDenied;
